'use client';

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

interface Props {
  /** Contenu encodé dans le QR (souvent une URL). */
  value: string;
  /** Côté du QR en pixels. */
  size?: number;
  /** Marge blanche (en modules), 0–4. */
  margin?: number;
  /** Niveau de correction d'erreur (défaut M ; les cartes imprimées utilisent Q). */
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  /** Couleurs des modules (`dark`) et du fond (`light`), hex. Défaut : noir sur blanc. */
  color?: { dark: string; light: string };
  alt?: string;
  className?: string;
}

/**
 * QR code rendu en data-URL PNG côté client. La génération est instantanée
 * (< 50 ms pour ~50 caractères) et entièrement offline.
 */
export function QrCodeImage({
  value,
  size = 192,
  margin = 2,
  errorCorrectionLevel = 'M',
  color,
  alt = "QR code d'invitation",
  className,
}: Props) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(value, { errorCorrectionLevel, margin, width: size, color })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
    // Couleurs comparées par valeur : un objet recréé à chaque rendu ne relance rien.
  }, [value, size, margin, errorCorrectionLevel, color?.dark, color?.light]);

  if (!dataUrl) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          background: color?.light ?? '#f5f5f5',
          borderRadius: 8,
        }}
        aria-label="Génération du QR code…"
      />
    );
  }
  return <img src={dataUrl} alt={alt} width={size} height={size} className={className} />;
}
